import type { AdCampaign, DashboardMetrics, Inquiry, Product } from "@kineslima/types";

export const productImages = [
  "/images/model-black.jpg",
  "/images/model-lace.jpg",
  "/images/model-red.jpg",
] as const;

const baseProducts: Array<Omit<Product, "id" | "uuid" | "slug" | "views" | "inquiries" | "createdAt">> = [
  {
    name: "Noir Élan",
    shortDescription: "Set de encaje negro con tiras satinadas y ajuste regulable.",
    description: "Un conjunto de encaje floral pensado para realzar la silueta con comodidad. El top incluye tirantes regulables y la pieza inferior ofrece un calce suave y seguro.",
    category: "Sets", categoryId: 1, price: 149, compareAtPrice: 189, currency: "PEN",
    sizes: ["S", "M", "L"], colors: ["Negro"], material: "Encaje de poliamida y elastano",
    care: "Lavado a mano con agua fría. No usar secadora.", image: productImages[0], gallery: [productImages[0], productImages[1]],
    stock: 14, status: "ACTIVE", featured: true, privacyProtected: true,
  },
  {
    name: "Corset Minuit",
    shortDescription: "Corset negro de encaje con estructura suave y acabado satinado.",
    description: "Corset de inspiración clásica con copa estructurada, paneles de encaje y cierre ajustable. Combina soporte ligero y un acabado sofisticado.",
    category: "Corsets", categoryId: 2, price: 179, currency: "PEN",
    sizes: ["S", "M", "L", "XL"], colors: ["Negro", "Vino"], material: "Encaje, microfibra y varillas flexibles",
    care: "Lavar por separado a mano. Secar a la sombra.", image: productImages[1], gallery: [productImages[1], productImages[0]],
    stock: 9, status: "ACTIVE", featured: true, privacyProtected: true,
  },
  {
    name: "Rouge Botanique",
    shortDescription: "Set bordado de rosas con liguero y detalles en negro.",
    description: "Una pieza protagonista con bordado floral, copa de soporte medio y tiras desmontables. Diseñada para ocasiones especiales sin renunciar al confort.",
    category: "Edición especial", categoryId: 3, price: 219, compareAtPrice: 249, currency: "PEN",
    sizes: ["S", "M", "L"], colors: ["Negro/Rojo"], material: "Tul bordado, encaje y elastano",
    care: "Lavado delicado a mano. Guardar extendido.", image: productImages[2], gallery: [productImages[2], productImages[0]],
    stock: 6, status: "ACTIVE", featured: true, privacyProtected: true,
  },
  {
    name: "Nuit Secrète",
    shortDescription: "Body negro con espalda de tiras y encaje geométrico.",
    description: "Body de líneas limpias y espalda protagonista. Sus tiras regulables permiten adaptar el calce y usarlo también como prenda exterior bajo una chaqueta.",
    category: "Bodies", categoryId: 4, price: 159, currency: "PEN",
    sizes: ["S", "M", "L"], colors: ["Negro", "Azul noche"], material: "Encaje elástico y microfibra",
    care: "Lavado a mano. No planchar.", image: productImages[0], gallery: [productImages[0], productImages[2]],
    stock: 18, status: "ACTIVE", featured: false, privacyProtected: true,
  },
  {
    name: "Velours",
    shortDescription: "Bralette de encaje suave con copa triangular y banda ancha.",
    description: "Bralette sin varillas con banda elástica suave y copa triangular forrada. Ligero para el día a día, elegante para cualquier ocasión.",
    category: "Bralettes", categoryId: 5, price: 99, currency: "PEN",
    sizes: ["S", "M", "L", "XL"], colors: ["Negro", "Marfil", "Vino"], material: "Encaje suave y algodón interior",
    care: "Lavar dentro de bolsa protectora en ciclo delicado.", image: productImages[1], gallery: [productImages[1], productImages[2]],
    stock: 22, status: "ACTIVE", featured: false, privacyProtected: true,
  },
  {
    name: "Fleur Ardente",
    shortDescription: "Conjunto de inspiración floral con bordado rojo y cortes modernos.",
    description: "Set de tres piezas con bordado de rosas, acabados en satén y herrajes dorados. Una colección limitada creada para destacar.",
    category: "Edición especial", categoryId: 3, price: 239, currency: "PEN",
    sizes: ["S", "M", "L"], colors: ["Rojo/Negro"], material: "Tul bordado, satén y elastano",
    care: "Lavar únicamente a mano con jabón neutro.", image: productImages[2], gallery: [productImages[2], productImages[1]],
    stock: 4, status: "ACTIVE", featured: false, privacyProtected: true,
  },
  {
    name: "Éclipse",
    shortDescription: "Set minimalista negro con tiras regulables y acabado mate.",
    description: "Diseño contemporáneo de cobertura media, con tirantes versátiles y tejido elástico de tacto suave.",
    category: "Sets", categoryId: 1, price: 129, currency: "PEN",
    sizes: ["XS", "S", "M", "L"], colors: ["Negro"], material: "Microfibra premium y elastano",
    care: "Lavar a mano con prendas de color similar.", image: productImages[0], gallery: [productImages[0], productImages[1]],
    stock: 0, status: "OUT_OF_STOCK", featured: false, privacyProtected: true,
  },
  {
    name: "Amour Sauvage",
    shortDescription: "Corset floral de edición limitada con liguero removible.",
    description: "Una silueta definida con bordado de flores, tiras ajustables y liguero desmontable para adaptar el look.",
    category: "Corsets", categoryId: 2, price: 199, compareAtPrice: 229, currency: "PEN",
    sizes: ["S", "M", "L"], colors: ["Negro/Rojo"], material: "Encaje bordado y satén",
    care: "Lavado delicado a mano. No retorcer.", image: productImages[2], gallery: [productImages[2], productImages[0]],
    stock: 7, status: "DRAFT", featured: false, privacyProtected: true,
  },
];

export const demoProducts: Product[] = baseProducts.map((product, index) => ({
  ...product,
  id: index + 1,
  uuid: `prd-${String(index + 1).padStart(3, "0")}`,
  slug: product.name.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, ""),
  views: 480 + index * 137,
  inquiries: 18 + index * 5,
  createdAt: `2026-07-${String(index + 1).padStart(2, "0")}T18:00:00Z`,
}));

export const demoInquiries: Inquiry[] = [
  { id: 1, uuid: "inq-001", productId: 3, productName: "Rouge Botanique", customerName: "María R.", phone: "+51 9•• ••• 142", email: "maria@example.com", size: "M", color: "Negro/Rojo", quantity: 1, total: 219, status: "NEW", createdAt: "2026-08-17T14:22:00Z" },
  { id: 2, uuid: "inq-002", productId: 1, productName: "Noir Élan", customerName: "Camila S.", phone: "+51 9•• ••• 876", size: "S", color: "Negro", quantity: 2, total: 298, status: "CONTACTED", createdAt: "2026-08-17T12:03:00Z" },
  { id: 3, uuid: "inq-003", productId: 2, productName: "Corset Minuit", customerName: "Valeria G.", phone: "+51 9•• ••• 031", size: "L", color: "Vino", quantity: 1, total: 179, status: "CONFIRMED", createdAt: "2026-08-16T20:41:00Z" },
  { id: 4, uuid: "inq-004", productId: 6, productName: "Fleur Ardente", customerName: "Lucía M.", phone: "+51 9•• ••• 520", size: "M", color: "Rojo/Negro", quantity: 1, total: 239, status: "DELIVERED", createdAt: "2026-08-15T18:12:00Z" },
];

export const demoAds: AdCampaign[] = [
  { id: 1, campaignName: "Colección Nocturne", title: "La noche empieza contigo", description: "Lanzamiento de temporada.", desktopImage: productImages[0], mobileImage: productImages[0], targetUrl: "/catalogo?coleccion=nocturne", ctaText: "Descubrir", placement: "HOME_MIDDLE", priority: 10, startDate: "2026-08-01T00:00:00Z", endDate: "2026-09-30T23:59:59Z", status: "ACTIVE", impressions: 18420, clicks: 612 },
  { id: 2, campaignName: "Corsets KinesLima", title: "Siluetas que hablan", desktopImage: productImages[1], mobileImage: productImages[1], targetUrl: "/catalogo?categoria=corsets", ctaText: "Ver corsets", placement: "CATALOG", priority: 8, startDate: "2026-08-10T00:00:00Z", endDate: "2026-10-10T23:59:59Z", status: "ACTIVE", impressions: 12870, clicks: 341 },
];

export const demoMetrics: DashboardMetrics = {
  products: 86, categories: 7, inquiries: 324, customers: 218, activeAds: 2,
  lowStock: 9, visits: 48290, impressions: 31290, adClicks: 953, estimatedSales: 48620,
};
